<template>
    <!-- D20: removal releases any license the person holds, and the copy says so without
         promising one to an unlicensed invitee. Same modal for the drawer and the Pending
         invitations page (D28). -->
    <ModalShell
        :title="`Remove ${employeeName}?`"
        subtitle="Their access ends, and any license they hold returns to the pool."
        width="w-full"
    >
        <template #icon>
            <PhUser :size="24" class="text-red-600" />
        </template>

        <p class="message-warning">
            <PhWarning :size="16" weight="fill" />
            This can't be undone.
        </p>

        <template #footer>
            <PrimeButton label="Cancel" severity="secondary" @click="closeDialog" :disabled="isLoading" />
            <PrimeButton
                label="Remove employee"
                severity="danger"
                :loading="isLoading"
                @click="confirmRemove"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import { PhUser, PhWarning } from '@phosphor-icons/vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';
import { getDialogRef } from '~/composables/dynamic-dialog';

const dialogRef = getDialogRef(ModalNames.REMOVE_EMPLOYEE);
const { showSuccess, showError } = useGlobalToast();

const companyId = computed(() => dialogRef.value?.data?.companyId as number | undefined);
const employeeId = computed(() => dialogRef.value?.data?.employeeId as number | undefined);
const employeeName = computed(() => dialogRef.value?.data?.employeeName as string || 'this employee');

const isLoading = ref(false);

function closeDialog() {
    dialogRef.value?.close();
}

async function confirmRemove() {
    if (!companyId.value || !employeeId.value) return;
    isLoading.value = true;
    try {
        await businessService.deleteEmployee(companyId.value, employeeId.value);
        showSuccess('Employee removed', `${employeeName.value} has been removed from your organization.`);
        dialogRef.value?.close({ removed: true });
    } catch (error: any) {
        showError('Error', error.message || 'Failed to remove employee');
    } finally {
        isLoading.value = false;
    }
}
</script>

<style lang="scss" scoped>
.message-warning {
    @apply m-0 flex items-center gap-1.5 text-sm font-medium text-red-600;
}
</style>
